import ChevronRightIcon from "@atlaskit/icon/core/chevron-right";
import {token} from "@atlaskit/tokens";
import type {Dispatch, MouseEvent, ReactNode, SetStateAction} from "react";
import {BottomDrawer} from "~/components/BottomDrawer";
import {
    getTableRowActionsMenuHandler,
    getTableRowActionsMenuItemIcon,
    TABLE_ROW_ACTIONS_MENU_ITEMS,
} from "~/components/table/tableRowActionsMenuItems";
import type {TableRowActionsConfig, TableRowActionsMenuItem} from "~/components/table/tableRowActions.types";
import {AnankeText} from "@vesta/ananke/components/anankeText";

function MenuDrawerRow({
    label,
    icon,
    isActive,
    isDisabled,
    onClick,
}: {
    label: string
    icon: ReactNode
    isActive?: boolean
    isDisabled?: boolean
    onClick: () => void
}) {
    return (
        <button
            type="button"
            disabled={isDisabled === true}
            onClick={onClick}
            className="w-full text-left"
            style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                gap: token("space.150", "12px"),
                height: "40px",
                minHeight: "40px",
                padding: `0 ${token("space.150", "12px")}`,
                borderRadius: "4px",
                border: "none",
                background:
                    isActive === true
                        ? token("color.background.selected")
                        : token("color.background.input"),
                color: token("color.text"),
                cursor: isDisabled === true ? "not-allowed" : "pointer",
                opacity: isDisabled === true ? 0.5 : 1,
                fontFamily: "Inter",
            }}
        >
            <span
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: token("space.150", "12px"),
                    minWidth: 0,
                }}
            >
                <span className="grid shrink-0 place-items-center">{icon}</span>
                <AnankeText color="color.text">{label}</AnankeText>
            </span>
            <ChevronRightIcon label="" color={token("color.icon.subtle")} />
        </button>
    );
}

export function TableRowActionsMenuDrawer<TRow>({
    open,
    setOpen,
    row,
    config,
    menuItemIds,
}: {
    open: boolean
    setOpen: Dispatch<SetStateAction<boolean>>
    row: TRow
    config: TableRowActionsConfig<TRow>
    menuItemIds: TableRowActionsMenuItem[]
}) {
    const activeState = config.getActiveState?.(row) ?? {};

    const runMenuAction = (item: TableRowActionsMenuItem) => {
        if (config.isMenuItemDisabled?.(item, row) === true) {
            return;
        }
        const handler = getTableRowActionsMenuHandler(item, config);
        if (handler === undefined) {
            return;
        }
        handler(row);
        setOpen(false);
    };

    return (
        <BottomDrawer
            open={open}
            setOpen={setOpen}
            title="Menu"
            fitContentOnOpen={true}
            headerBottomBorder={true}
            zIndex={200}
        >
            <div className="grid gap-ds-space-100 pb-ds-space-100" style={{fontFamily: "Inter"}}>
                {menuItemIds.map((itemId) => {
                    const item = TABLE_ROW_ACTIONS_MENU_ITEMS[itemId];
                    if (item === undefined) {
                        return null;
                    }
                    const isDisabled = config.isMenuItemDisabled?.(itemId, row) === true;
                    const label = config.menuItemLabels?.[itemId] ?? item.label;
                    const icon =
                        config.getMenuItemIcon?.(itemId, isDisabled) ??
                        getTableRowActionsMenuItemIcon(itemId, isDisabled);
                    return (
                        <MenuDrawerRow
                            key={itemId}
                            label={label}
                            icon={icon}
                            isActive={activeState[itemId] === true}
                            isDisabled={isDisabled}
                            onClick={() => {
                                runMenuAction(itemId);
                            }}
                        />
                    );
                })}
                {(config.extraMoreMenuItems?.(row) ?? []).map((item) => (
                    <MenuDrawerRow
                        key={item.id}
                        label={item.label}
                        icon={item.icon}
                        isDisabled={item.isDisabled === true}
                        onClick={() => {
                            if (item.isDisabled === true) {
                                return;
                            }
                            item.onClick();
                            setOpen(false);
                        }}
                    />
                ))}
                {config.renderMoreMenuItems !== undefined && config.extraMoreMenuItems === undefined ? (
                    <div
                        className="table-row-actions-menu-drawer-extra"
                        onClick={(event: MouseEvent) => {
                            event.stopPropagation();
                        }}
                    >
                        {config.renderMoreMenuItems(row)}
                    </div>
                ) : null}
            </div>
        </BottomDrawer>
    );
}
